import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, List, ListItemButton, Popover, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FloatingFieldShell from './FloatingFieldShell';

const ReportSingleSelectFilter = ({
  label,
  options = [],
  value,
  onChange,
  width = 160,
  disabled = false,
  required = false,
  error = false,
  id,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const selectedOption = options.find((option) => option.value === value) ?? null;

  const handleOpen = (event) => {
    if (disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <FloatingFieldShell
      label={label}
      disabled={disabled}
      active={open}
      floated={open || Boolean(selectedOption)}
      required={required}
      error={error}
      sx={{ width, minWidth: width, flexShrink: 0 }}
    >
      <Box
        id={id}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        tabIndex={disabled ? -1 : 0}
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen(event);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          height: '100%',
          px: 1.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: 12 }}>
          {selectedOption?.label ?? ''}
        </Typography>
        <KeyboardArrowDownIcon fontSize="small" sx={{ opacity: 0.5, flexShrink: 0 }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorEl?.offsetWidth ? Math.max(anchorEl.offsetWidth, 240) : 240,
              borderRadius: 1.5,
              overflow: 'hidden',
            },
          },
        }}
      >
        <List dense disablePadding sx={{ maxHeight: 260, overflowY: 'auto', py: 0.5 }}>
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <ListItemButton
                key={option.value}
                selected={isSelected}
                onClick={() => {
                  onChange(option.value);
                  handleClose();
                }}
                sx={{
                  py: 0.875,
                  px: 1.5,
                  '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
                  '&.Mui-selected, &.Mui-selected:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                  {option.label}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Popover>
    </FloatingFieldShell>
  );
};

export default ReportSingleSelectFilter;
